import { useLingui } from "@lingui/react";
import { CheckoutSettings } from "@paddle/paddle-js/types/checkout/checkout";
import { IApiEpBillingProductPurchaseRes } from "@smp/api";
import { clsx } from "clsx";
import { useCallback, useRef } from "react";
import { useUpdateEffect } from "react-use";

import { usePaddle } from "@/hooks/use_paddle";
import { isRecurringPayment } from "@/screens/app/components/payment_v2/payment_v2.static";
import { pickPaddleLocale } from "@/utils";

import classes from "./paddle.module.css";

const CONTAINER_CLASS = "ep-paddle-container";

export const Paddle = ({
  invoice,
  onDecline,
  onSuccess,
}: {
  invoice: IApiEpBillingProductPurchaseRes;
  onDecline: () => void;
  onSuccess?: () => void;
}) => {
  const {
    i18n: { locale },
  } = useLingui();

  const $container = useRef<HTMLDivElement | null>(null);

  const eventListener = useCallback<
    NonNullable<Parameters<typeof usePaddle>[0]>
  >(
    (event) => {
      if (event.name === "checkout.completed" && onSuccess) {
        onSuccess();
      } else if (event.name === "checkout.payment.failed") {
        onDecline();
      }
    },
    [onDecline, onSuccess],
  );

  const paddle = usePaddle(eventListener);

  useUpdateEffect(() => {
    if (paddle && $container.current) {
      const settings: CheckoutSettings = {
        variant: "one-page",
        displayMode: "inline",
        frameTarget: $container.current.classList.toString(),
        frameStyle: " ",
        locale: pickPaddleLocale(locale),
      };

      const recurringPayment = isRecurringPayment(invoice)
        ? invoice.recurring_payment
        : undefined;
      const bill = !isRecurringPayment(invoice) ? invoice.bill : undefined;

      if (
        recurringPayment?.confirm_widget_args?.items &&
        recurringPayment?.confirm_widget_args?.customer &&
        recurringPayment?.confirm_widget_args?.customData
      ) {
        paddle.Checkout.open({
          items: recurringPayment.confirm_widget_args.items,
          customer: recurringPayment.confirm_widget_args.customer,
          customData: recurringPayment.confirm_widget_args.customData,
          settings,
        });
      } else if (bill?.extra.paddle_widget_args) {
        paddle.Checkout.open({
          ...bill.extra.paddle_widget_args,
          settings,
        });
      } else {
        onDecline();
      }
    }
  }, [paddle]);

  return (
    <div
      className={clsx(CONTAINER_CLASS, classes.container)}
      ref={$container}
    />
  );
};
